import React, { useEffect, useState, useCallback } from 'react';
import {
  FaMobileAlt, FaDesktop, FaFont, FaPalette,
  FaAlignLeft, FaToggleOn, FaToggleOff
} from 'react-icons/fa';
import './captioneditor.css';

const FONTS = [
  { value: 'Arial',            label: 'Arial' },
  { value: 'Helvetica',        label: 'Helvetica' },
  { value: 'Impact',           label: 'Impact' },
  { value: 'Georgia',          label: 'Georgia' },
  { value: "'Times New Roman'", label: 'Times New Roman' },
  { value: "'Courier New'",    label: 'Courier New' },
  { value: 'Verdana',          label: 'Verdana' },
  { value: "'Trebuchet MS'",   label: 'Trebuchet MS' },
  { value: "'Comic Sans MS'",  label: 'Comic Sans MS' },
];

const DEFAULT_SETTINGS = {
  position: 'bottom',
  fontSize: 52,
  fontFamily: 'Arial',
  fontWeight: 'bold',
  textColor: '#FFFFFF',
  highlightColor: '#FFD700',
  outlineColor: '#000000',
  backgroundColor: '#000000',
  backgroundOpacity: 0,
  outlineWidth: 0,
  alignment: 'center',
  wordsPerGroup: 3,
  style: 'karaoke',
};

const PREVIEW_TEXT = "They say Santa Claus isn't real";

const CaptionEditor = ({
  onSettingsChange,
  onEnabledChange,
  captionsEnabled: initialEnabled = false,
  captionSettings: initialSettings,
}) => {
  const [captionsEnabled, setCaptionsEnabled] = useState(initialEnabled);
  const [captionSettings, setCaptionSettings] = useState(initialSettings || DEFAULT_SETTINGS);
  const [deviceOrientation, setDeviceOrientation] = useState('landscape');

  // Sync from parent if it changes
  useEffect(() => { setCaptionsEnabled(initialEnabled); }, [initialEnabled]);
  useEffect(() => { if (initialSettings) setCaptionSettings(initialSettings); }, [initialSettings]);

  // Notify parent on change
  useEffect(() => { onEnabledChange?.(captionsEnabled); }, [captionsEnabled]);
  useEffect(() => { onSettingsChange?.(captionSettings); }, [captionSettings]);

  const handleSettingChange = useCallback((key, value) => {
    setCaptionSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const toggleCaptions = useCallback(() => {
    setCaptionsEnabled(prev => !prev);
  }, []);

  // ── Caption style for preview ────────────────────────────────
  const scaleFactor = deviceOrientation === 'portrait' ? 0.42 : 0.56;
  const bgHex = Math.round(captionSettings.backgroundOpacity * 2.55).toString(16).padStart(2, '0');
  const hasBg = captionSettings.backgroundOpacity > 0;

  const captionBaseStyle = {
    fontFamily: `${captionSettings.fontFamily}, sans-serif`,
    fontWeight: captionSettings.fontWeight,
    fontSize: `${Math.round(captionSettings.fontSize * scaleFactor)}px`,
    WebkitTextStroke: `${captionSettings.outlineWidth}px ${captionSettings.outlineColor}`,
    textAlign: captionSettings.alignment,
    backgroundColor: `${captionSettings.backgroundColor}${bgHex}`,
    padding: hasBg ? '6px 14px' : '0',
    borderRadius: hasBg ? '6px' : '0',
    lineHeight: 1.3,
    maxWidth: '90%',
    textShadow: '1px 1px 3px rgba(0,0,0,0.9)',
  };

  // ── Summary chips for preview-info ───────────────────────────
  const infoPairs = captionsEnabled
    ? [
        ['Style', captionSettings.style],
        ['Position', captionSettings.position],
        ['Font', `${captionSettings.fontFamily.replace(/'/g, '')} ${captionSettings.fontSize}px`],
        ['Weight', captionSettings.fontWeight],
        ['Alignment', captionSettings.alignment],
        ...(captionSettings.style !== 'word-by-word'
          ? [['Words/Group', captionSettings.wordsPerGroup]]
          : []),
        ...(captionSettings.outlineWidth > 0
          ? [['Outline', `${captionSettings.outlineWidth}px`]]
          : []),
        ...(captionSettings.backgroundOpacity > 0
          ? [['BG Opacity', `${captionSettings.backgroundOpacity}%`]]
          : []),
      ]
    : [['Captions', 'Disabled']];

  return (
    <div className="caption-editor">
      <div className="editor-container">

        {/* ─── LEFT: CONTROLS ───────────────────────────────── */}
        <div className="controls-panel">

          {/* Header */}
          <div className="caption-panel-header">
            <h3 className="panel-title">Caption Settings</h3>
            <button
              className={`caption-toggle ${captionsEnabled ? 'enabled' : 'disabled'}`}
              onClick={toggleCaptions}
              aria-pressed={captionsEnabled}
            >
              {captionsEnabled ? <FaToggleOn /> : <FaToggleOff />}
              <span>{captionsEnabled ? 'On' : 'Off'}</span>
            </button>
          </div>

          {captionsEnabled && (
            <>
              {/* ── Style ──────────────────────────────────── */}
              <div className="control-group">
                <label className="control-label">
                  <FaFont /> Caption Style
                </label>
                <select
                  value={captionSettings.style}
                  onChange={(e) => handleSettingChange('style', e.target.value)}
                  className="control-select"
                >
                  <option value="karaoke">Karaoke (Word Highlight)</option>
                  <option value="grouped">Grouped</option>
                  <option value="word-by-word">Word by Word</option>
                </select>
              </div>

              {/* ── Position ───────────────────────────────── */}
              <div className="control-group">
                <label className="control-label">
                  <FaAlignLeft /> Position
                </label>
                <div className="position-buttons">
                  {['top', 'middle', 'bottom'].map(pos => (
                    <button
                      key={pos}
                      className={`pos-btn ${captionSettings.position === pos ? 'active' : ''}`}
                      onClick={() => handleSettingChange('position', pos)}
                    >
                      {pos.charAt(0).toUpperCase() + pos.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Words Per Group ────────────────────────── */}
              {captionSettings.style !== 'word-by-word' && (
                <div className="control-group">
                  <label className="control-label">
                    Words per Group: {captionSettings.wordsPerGroup}
                  </label>
                  <input
                    type="range"
                    min="2"
                    max="10"
                    value={captionSettings.wordsPerGroup}
                    onChange={(e) => handleSettingChange('wordsPerGroup', parseInt(e.target.value))}
                    className="control-slider"
                  />
                </div>
              )}

              {/* ── Font Family ────────────────────────────── */}
              <div className="control-group">
                <label className="control-label">
                  <FaFont /> Font Family
                </label>
                <select
                  value={captionSettings.fontFamily}
                  onChange={(e) => handleSettingChange('fontFamily', e.target.value)}
                  className="control-select"
                  style={{ fontFamily: `${captionSettings.fontFamily}, sans-serif` }}
                >
                  {FONTS.map(f => (
                    <option key={f.value} value={f.value} style={{ fontFamily: `${f.value}, sans-serif` }}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* ── Font Weight ────────────────────────────── */}
              <div className="control-group">
                <label className="control-label">Font Weight</label>
                <select
                  value={captionSettings.fontWeight}
                  onChange={(e) => handleSettingChange('fontWeight', e.target.value)}
                  className="control-select"
                >
                  <option value="lighter">Light</option>
                  <option value="normal">Normal</option>
                  <option value="bold">Bold</option>
                  <option value="bolder">Extra Bold</option>
                </select>
              </div>

              {/* ── Font Size ──────────────────────────────── */}
              <div className="control-group">
                <label className="control-label">
                  Font Size: <span style={{ color: '#a78bfa', marginLeft: 4 }}>{captionSettings.fontSize}px</span>
                </label>
                <input
                  type="range"
                  min="24"
                  max="80"
                  value={captionSettings.fontSize}
                  onChange={(e) => handleSettingChange('fontSize', parseInt(e.target.value))}
                  className="control-slider"
                />
              </div>

              {/* ── Colors ─────────────────────────────────── */}
              <div className="control-group">
                <label className="control-label">
                  <FaPalette /> Colors
                </label>
                <div className="color-controls">
                  <div className="color-input-group">
                    <label className="color-label">Text</label>
                    <input
                      type="color"
                      value={captionSettings.textColor}
                      onChange={(e) => handleSettingChange('textColor', e.target.value)}
                      className="color-picker"
                      title="Text color"
                    />
                  </div>
                  {captionSettings.style === 'karaoke' && (
                    <div className="color-input-group">
                      <label className="color-label">Highlight</label>
                      <input
                        type="color"
                        value={captionSettings.highlightColor}
                        onChange={(e) => handleSettingChange('highlightColor', e.target.value)}
                        className="color-picker"
                        title="Highlight color"
                      />
                    </div>
                  )}
                  <div className="color-input-group">
                    <label className="color-label">Outline</label>
                    <input
                      type="color"
                      value={captionSettings.outlineColor}
                      onChange={(e) => handleSettingChange('outlineColor', e.target.value)}
                      className="color-picker"
                      title="Outline color"
                    />
                  </div>
                  <div className="color-input-group">
                    <label className="color-label">BG</label>
                    <input
                      type="color"
                      value={captionSettings.backgroundColor}
                      onChange={(e) => handleSettingChange('backgroundColor', e.target.value)}
                      className="color-picker"
                      title="Background color"
                    />
                  </div>
                </div>
              </div>

              {/* ── Outline Width ──────────────────────────── */}
              <div className="control-group">
                <label className="control-label">
                  Outline Width: <span style={{ color: '#a78bfa', marginLeft: 4 }}>{captionSettings.outlineWidth}px</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="6"
                  value={captionSettings.outlineWidth}
                  onChange={(e) => handleSettingChange('outlineWidth', parseInt(e.target.value))}
                  className="control-slider"
                />
              </div>

              {/* ── Background Opacity ─────────────────────── */}
              <div className="control-group">
                <label className="control-label">
                  Background Opacity: <span style={{ color: '#a78bfa', marginLeft: 4 }}>{captionSettings.backgroundOpacity}%</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={captionSettings.backgroundOpacity}
                  onChange={(e) => handleSettingChange('backgroundOpacity', parseInt(e.target.value))}
                  className="control-slider"
                />
              </div>

              {/* ── Alignment ──────────────────────────────── */}
              <div className="control-group">
                <label className="control-label">Text Alignment</label>
                <div className="alignment-buttons">
                  {['left', 'center', 'right'].map(align => (
                    <button
                      key={align}
                      className={`align-btn ${captionSettings.alignment === align ? 'active' : ''}`}
                      onClick={() => handleSettingChange('alignment', align)}
                    >
                      {align.charAt(0).toUpperCase() + align.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* ─── RIGHT: PREVIEW ───────────────────────────────── */}
        <div className="preview-panel">

          {/* Device toggle */}
          <div className="device-toggle">
            <button
              className={`device-btn ${deviceOrientation === 'landscape' ? 'active' : ''}`}
              onClick={() => setDeviceOrientation('landscape')}
            >
              <FaDesktop /> Landscape
            </button>
            <button
              className={`device-btn ${deviceOrientation === 'portrait' ? 'active' : ''}`}
              onClick={() => setDeviceOrientation('portrait')}
            >
              <FaMobileAlt /> Portrait
            </button>
          </div>

          {/* Device screen */}
          <div className={`device-preview ${deviceOrientation}`}>
            <div className="device-screen">
              {/* Fake video background */}
              <div className="video-mockup">
                <div className="mockup-content">
                  <div className="mockup-circle" />
                  <div className="mockup-text">Video Preview</div>
                </div>
              </div>

              {/* Caption overlay */}
              {captionsEnabled && (
                <div className={`caption-overlay position-${captionSettings.position}`}>
                  {captionSettings.style === 'karaoke' ? (
                    <div className="caption-text" style={captionBaseStyle}>
                      <span style={{
                        color: captionSettings.highlightColor,
                        fontFamily: `${captionSettings.fontFamily}, sans-serif`,
                        fontWeight: captionSettings.fontWeight,
                      }}>
                        They say{' '}
                      </span>
                      <span style={{
                        color: captionSettings.textColor,
                        fontFamily: `${captionSettings.fontFamily}, sans-serif`,
                        fontWeight: captionSettings.fontWeight,
                      }}>
                        Santa Claus isn't real
                      </span>
                    </div>
                  ) : (
                    <div className="caption-text" style={{ ...captionBaseStyle, color: captionSettings.textColor }}>
                      {PREVIEW_TEXT}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Info chips */}
          <div className="preview-info">
            {infoPairs.map(([key, val]) => (
              <p key={key}>
                <strong>{key}:</strong>
                {key === 'Style' || key === 'Position' || key === 'Captions'
                  ? <span style={{ color: '#a78bfa', marginLeft: 4, fontWeight: 600 }}>{val}</span>
                  : <span style={{ marginLeft: 4 }}>{val}</span>
                }
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaptionEditor;